import type { Agent, AgentGroup, AgentInput, AgentStatus, GroupInput, SkillScope, AgentProvider } from "@codvero/shared";
import { api, del, get, post } from "./client.ts";

export const studioApi = {
  agents: () => get<Agent[]>("/api/studio/agents"),
  agent: (id: string) => get<Agent>(`/api/studio/agents/${id}`),
  createAgent: (input: AgentInput) => post<Agent>("/api/studio/agents", input),
  updateAgent: (id: string, input: AgentInput) => api<Agent>(`/api/studio/agents/${id}`, { method: "PUT", json: input }),
  setStatus: (id: string, status: AgentStatus) => post<Agent>(`/api/studio/agents/${id}/status`, { status }),
  clone: (id: string) => post<Agent>(`/api/studio/agents/${id}/clone`),
  removeAgent: (id: string) => del(`/api/studio/agents/${id}`),
  groups: () => get<AgentGroup[]>("/api/studio/groups"),
  createGroup: (input: GroupInput) => post<AgentGroup>("/api/studio/groups", input),
  updateGroup: (id: string, input: GroupInput) => api<AgentGroup>(`/api/studio/groups/${id}`, { method: "PUT", json: input }),
  removeGroup: (id: string) => del(`/api/studio/groups/${id}`),
};

import type { CollectionUpdateResult, ReviewSkillInput, Skill, SkillCollection } from "@codvero/shared";
export const skillsApi = {
  list: (scope: SkillScope = "studio") => get<Skill[]>(`/api/studio/skills${scope === "chat" ? "?scope=chat" : ""}`),
  upload: async (file: File, scope: SkillScope = "studio") => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("scope", scope);
    const res = await fetch("/api/studio/skills/upload", { method: "POST", body: fd, credentials: "same-origin" });
    const body = await res.json();
    if (!res.ok) throw Object.assign(new Error(body.message ?? body.error), { code: body.error, status: res.status });
    return body as Skill[];
  },
  github: (url: string, ref?: string, scope: SkillScope = "studio") => post<Skill[]>("/api/studio/skills/github", { url, scope, ...(ref ? { ref } : {}) }),
  collections: (scope: SkillScope = "studio") => get<SkillCollection[]>(`/api/studio/skills/collections${scope === "chat" ? "?scope=chat" : ""}`),
  /** Chat skills: the providers that get it (null = all). */
  setProviders: (id: string, providers: AgentProvider[] | null) => api<Skill>(`/api/studio/skills/${id}/providers`, { method: "PUT", json: { providers } }),
  checkCollection: (id: string) => post<SkillCollection>(`/api/studio/skills/collections/${id}/check`),
  updateCollection: (id: string) => post<CollectionUpdateResult>(`/api/studio/skills/collections/${id}/update`),
  removeCollection: (id: string) => del(`/api/studio/skills/collections/${id}`),
  deliver: (id: string, projectId: string) => post<{ path: string; slug: string }>(`/api/studio/skills/${id}/deliver`, { projectId }),
  review: (id: string, input: ReviewSkillInput) => post<Skill>(`/api/studio/skills/${id}/review`, input),
  archive: (id: string, archived: boolean) => post<Skill>(`/api/studio/skills/${id}/archive`, { archived }),
  remove: (id: string) => del(`/api/studio/skills/${id}`),
};

import type { Mentionable, TeamTask } from "@codvero/shared";
export const teamApi = {
  mentionables: (projectId: string) => get<Mentionable[]>(`/api/projects/${projectId}/mentionables`),
  tasks: (chatId: string) => get<TeamTask[]>(`/api/chats/${chatId}/tasks`),
  approve: (id: string) => post<TeamTask>(`/api/tasks/${id}/approve`),
  reject: (id: string) => post<TeamTask>(`/api/tasks/${id}/reject`),
};
